import {Component, EventEmitter, Input, OnChanges, Output, ChangeDetectionStrategy} from '@angular/core';
import {Step} from '../../../models/convo';
import {User} from '../../../../shared/models/user';
import {StepId} from '../../../models/uuid';
import {Alias} from '../../../models/llm/llm';
import {ConvoBubbleOptionsComponent} from '../convo-bubble-options/convo-bubble-options.component';
import {UserAvatarComponent} from '../../../../shared/components/user-avatar/user-avatar.component';
import {UsersService} from '../../../../core/services/users.service';


@Component({
  selector: 'moti-convo-step-metadata',
  templateUrl: './convo-step-metadata.component.html',
  styleUrl: './convo-step-metadata.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ConvoBubbleOptionsComponent,
    UserAvatarComponent,
  ],
})
export class ConvoStepMetadataComponent implements OnChanges {
  @Input() step: Step;
  @Input() showModel: boolean = false;
  @Input() showActions: boolean = true;
  @Input() readyToAction: boolean = false;
  @Input() user: User; // Current logged-in user (for fallback)
  @Input() rtl: boolean;
  @Input() isLatestStep: boolean = false;

  @Input() deletingStep: boolean;
  @Output() clickedDelete$: EventEmitter<StepId> = new EventEmitter<StepId>();

  @Input() addingStep: boolean;
  @Output() clickedAdd$: EventEmitter<Alias> = new EventEmitter<Alias>();

  displayUser: User; // The user to display (either step creator or current user)
  isCurrentUser: boolean = false; // Whether the step is from the current logged-in user


  constructor(
    private usersService: UsersService,
  ) {
  }

  ngOnChanges(): void {
    if (this.step && !this.step.createdByProvider) {
      this.fetchStepCreator();
    } else {
      // Reset for provider messages
      this.isCurrentUser = false;
    }
  }

  private fetchStepCreator(): void {
    // Check if it's the current user first (avoid unnecessary API call)
    if (this.user && String(this.step.createdBy) === String(this.user.ID)) {
      this.displayUser = this.user;
      this.isCurrentUser = true;
      return;
    }

    // Fetch the user who created this step (HTTP observable auto-completes)
    this.usersService.fetchUser$(this.step.createdBy).subscribe({
      next: (user) => {
        this.displayUser = user;
        // Convert both IDs to strings for comparison to handle type mismatches
        this.isCurrentUser = this.user && String(user.ID) === String(this.user.ID);
      }
    });
  }

  formatTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    const now = new Date();
    
    // Check if the message is from today
    const isToday = date.getDate() === now.getDate() &&
                    date.getMonth() === now.getMonth() &&
                    date.getFullYear() === now.getFullYear();
    
    if (isToday) {
      // For today's messages, show time in HH:MM format
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } else {
      // For older messages, show full date in MM/DD/YYYY HH:MM format
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${month}/${day}/${year} ${hours}:${minutes}`;
    }
  }
}
